import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Heart,
  MessageCircle,
  Share2,
  MapPin,
  Clock,
  X,
  Send,
} from "lucide-react";
import WaveSurfer from "wavesurfer.js";
import "./Carousel.css";
import kleidoLogo from "../../../assets/images/kleido-whbk.png";

// ================================================================
// AUDIO WAVEFORM COMPONENT
// ================================================================
const AudioWaveform = ({ src }) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);

  useEffect(() => {
    if (waveformRef.current && !wavesurferRef.current) {
      wavesurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: "#888",
        progressColor: "#a78bfa",
        cursorColor: "#a78bfa",
        height: 100,
        responsive: true,
      });
      wavesurferRef.current.load(src);
    }
    return () => {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }
    };
  }, [src]);

  return <div ref={waveformRef} className="waveform-container"></div>;
};

// ================================================================
// MAIN CAROUSEL COMPONENT
// ================================================================
const Carousel = ({ items, postData, onAddComment, onMediaLike, currentUserId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [detailMedia, setDetailMedia] = useState(null);

  // ---- Like state (double-click toggle) ----
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(postData?.likes?.length || postData?.likes || 0);

  // ---- Comments state (local copy so new comments appear immediately) ----
  const [comments, setComments] = useState(postData?.comments || []);
  const [newComment, setNewComment] = useState("");

  // ---- Reply state ----
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  // ---- Share / Suggest state ----
  const [shareCount, setShareCount] = useState(
    Array.isArray(postData?.shares) ? postData.shares.length : postData?.shares || 0
  );
  const [suggestCount, setSuggestCount] = useState(postData?.suggestions || 0);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestUserId, setSuggestUserId] = useState("");
  const [toastMsg, setToastMsg] = useState("");

  // ---- Editable metadata fields ----
  const [mediaLabel, setMediaLabel] = useState(postData?.mediaLabel || "Human Curated");
  const [behavioralDepth, setBehavioralDepth] = useState(postData?.behavioralDepth || "Very High");

  const qualityRating = postData?.qualityRating || 0;
  const shareDropdownRef = useRef(null);

  // ---- Memoized normalized items ----
  const normalizedItems = useMemo(() => {
    return items?.map((item) => ({
      ...item,
      likes: Array.isArray(item.likes) ? item.likes : [],
      userLiked: Array.isArray(item.likes)
        ? item.likes.some((like) => like.userId === currentUserId || like === currentUserId)
        : false,
    })) || [];
  }, [items, currentUserId]);

  // ---- Close share dropdown on outside click ----
  useEffect(() => {
    const handleClick = (e) => {
      if (shareDropdownRef.current && !shareDropdownRef.current.contains(e.target)) {
        setShowShareDropdown(false);
      }
    };
    if (showShareDropdown) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showShareDropdown]);

  // ---- Auto-dismiss toast ----
  useEffect(() => {
    if (toastMsg) {
      const timer = setTimeout(() => setToastMsg(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMsg]);

  // ================================================================
  // TIME HELPER
  // ================================================================
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return "Just now";
    const now = new Date();
    const posted = new Date(timestamp);
    const diff = Math.floor((now - posted) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  // ================================================================
  // HANDLERS
  // ================================================================

  // ---- Add comment (top-level) ----
  const handleAddComment = () => {
    const trimmed = newComment.trim();
    if (!trimmed) return;

    const newCommentObj = {
      userId: currentUserId || postData?.currentUserId || "You",
      comment: trimmed,
      timestamp: new Date().toISOString(),
      replies: [],
    };

    setComments((prev) => [...prev, newCommentObj]);

    if (onAddComment) {
      onAddComment(postData?.postId, trimmed);
    }
    setNewComment("");
  };

  // ---- Double-click like toggle ----
  const handleDoubleLike = (e) => {
    e.stopPropagation();
    if (currentUserId === postData?.userId) {
      console.warn("You cannot like your own post");
      return;
    }
    if (isLiked) {
      setIsLiked(false);
      setLikeCount((prev) => Math.max(0, prev - 1));
    } else {
      setIsLiked(true);
      setLikeCount((prev) => prev + 1);
    }
  };

  // ---- Reply handlers ----
  const handleReplyClick = (index) => {
    if (replyingTo === index) {
      setReplyingTo(null);
      setReplyText("");
    } else {
      setReplyingTo(index);
      setReplyText("");
    }
  };

  const handleReplySubmit = (parentIndex) => {
    const trimmed = replyText.trim();
    if (!trimmed) return;

    const parentComment = comments[parentIndex];
    const newReply = {
      userId: currentUserId || postData?.currentUserId || "You",
      comment: trimmed,
      timestamp: new Date().toISOString(),
      replyTo: parentComment?.userId || null,
    };

    setComments((prev) =>
      prev.map((c, i) =>
        i === parentIndex
          ? { ...c, replies: [...(c.replies || []), newReply] }
          : c
      )
    );

    if (onAddComment) {
      onAddComment(postData?.postId, trimmed, parentComment?.userId);
    }

    setReplyText("");
    setReplyingTo(null);
  };

  // ---- Share handler ----
  const shareUrl = postData?.shareUrl || (typeof window !== "undefined" ? window.location.href : "");
  const shareText = postData?.descriptions || "Check out this content on Kleido!";

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => {});
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(textarea);
    }
  };

  const handleShare = (platform) => {
    setShowShareDropdown(false);
    setShareCount((prev) => prev + 1);

    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);

    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
          "_blank", "noopener,noreferrer,width=600,height=400"
        );
        break;
      case "instagram":
        copyToClipboard(shareUrl);
        setToastMsg("Link copied! Paste it in Instagram.");
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
        break;
      case "snapchat":
        copyToClipboard(shareUrl);
        setToastMsg("Link copied! Paste it in Snapchat.");
        window.open("https://www.snapchat.com/", "_blank", "noopener,noreferrer");
        break;
      case "tiktok":
        copyToClipboard(shareUrl);
        setToastMsg("Link copied! Paste it in TikTok.");
        window.open("https://www.tiktok.com/", "_blank", "noopener,noreferrer");
        break;
      case "native":
        if (navigator.share) {
          navigator.share({ title: "Kleido Content", text: shareText, url: shareUrl }).catch(() => {});
        } else {
          copyToClipboard(shareUrl);
          setToastMsg("Link copied to clipboard!");
        }
        break;
      default:
        break;
    }
  };

  // ---- Suggest handler ----
  const handleSuggestSubmit = () => {
    const trimmed = suggestUserId.trim();
    if (!trimmed) {
      setToastMsg("Please enter a valid User ID.");
      return;
    }
    console.log(`[Suggest] Post ${postData?.postId} suggested to user: ${trimmed}`);
    setSuggestCount((prev) => prev + 1);
    setSuggestUserId("");
    setShowSuggestModal(false);
    setToastMsg(`Content suggested to ${trimmed}!`);
  };

  // ================================================================
  // STAR RATING RENDERER
  // ================================================================
  const renderStars = (rating) => (
    <div className="mm-quality-stars" title={`Quality: ${rating}/5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <svg key={star} width="18" height="18" viewBox="0 0 24 24"
          fill={star <= rating ? "#FFD700" : "none"}
          stroke={star <= rating ? "#FFD700" : "rgba(255,215,0,0.3)"}
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
      <span className="mm-quality-label">{rating}/5</span>
    </div>
  );

  // ================================================================
  // SHARE DROPDOWN RENDERER
  // ================================================================
  const renderShareDropdown = () => (
    <div className="mm-share-dropdown" ref={shareDropdownRef}>
      <button className="mm-share-option" onClick={() => handleShare("facebook")}>
        <span className="mm-share-icon mm-share-icon--facebook">f</span>
        <span>Facebook</span>
      </button>
      <button className="mm-share-option" onClick={() => handleShare("instagram")}>
        <span className="mm-share-icon mm-share-icon--instagram">📷</span>
        <span>Instagram</span>
      </button>
      <button className="mm-share-option" onClick={() => handleShare("snapchat")}>
        <span className="mm-share-icon mm-share-icon--snapchat">👻</span>
        <span>Snapchat</span>
      </button>
      <button className="mm-share-option" onClick={() => handleShare("tiktok")}>
        <span className="mm-share-icon mm-share-icon--tiktok">♪</span>
        <span>TikTok</span>
      </button>
      {typeof navigator !== "undefined" && navigator.share && (
        <button className="mm-share-option" onClick={() => handleShare("native")}>
          <span className="mm-share-icon">📱</span>
          <span>More...</span>
        </button>
      )}
    </div>
  );

  // ================================================================
  // SUGGEST MODAL RENDERER
  // ================================================================
  const renderSuggestModal = () => (
    <div className="mm-suggest-overlay"
      onClick={(e) => { if (e.target.classList.contains("mm-suggest-overlay")) setShowSuggestModal(false); }}>
      <div className="mm-suggest-modal">
        <button className="mm-suggest-close" onClick={() => setShowSuggestModal(false)} aria-label="Close">✕</button>
        <h3 className="mm-suggest-title">Suggest to User</h3>
        <p className="mm-suggest-help">
          Enter the User ID of the person you want to suggest this content to.
          Anyone with a valid Kleido User ID can receive it.
        </p>
        <div className="mm-suggest-input-row">
          <label className="mm-suggest-label">User ID</label>
          <input type="text" value={suggestUserId} placeholder="e.g. user123"
            onChange={(e) => setSuggestUserId(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSuggestSubmit()}
            className="mm-suggest-input" autoFocus
            style={{ background: "rgba(36,36,45,0.2)", color: "white" }} />
        </div>
        <div className="mm-suggest-actions">
          <button className="mm-suggest-cancel" onClick={() => setShowSuggestModal(false)}>Cancel</button>
          <button className="mm-suggest-submit" onClick={handleSuggestSubmit} disabled={!suggestUserId.trim()}
            style={{ background: "rgba(36,36,45,0.2)", color: "white", border: "1px solid gray" }}>
            Send Suggestion
          </button>
        </div>
      </div>
    </div>
  );

  // ================================================================
  // COMMENT RENDERER (with Reply button + nested replies)
  // ================================================================
  const renderComment = (comment, index) => (
    <div key={index} className="mm-comment-thread">
      {/* Parent comment */}
      <div className="mm-modal-comment">
        <div className="mm-comment-body" style={{ gap: 8 }}>
          <strong className="mm-modal-comment-user" style={{ color: "white" }}>{comment.userId}:</strong>
          <span className="mm-modal-comment-text" style={{ marginLeft: 8, color: "white" }}>{comment.comment}</span>
          <span className="mm-modal-comment-time" style={{ marginLeft: 8, color: "rgba(255,255,255,0.4)", fontSize: 11 }}>
            {comment.timestamp ? getTimeAgo(comment.timestamp) : ""}
          </span>
        </div>
        <button
          className={`mm-reply-btn ${replyingTo === index ? "mm-reply-btn--active" : ""}`}
          onClick={() => handleReplyClick(index)}
          aria-label="Reply to this comment">
          {replyingTo === index ? "Cancel" : "Reply"}
        </button>
      </div>

      {/* Inline reply input */}
      {replyingTo === index && (
        <div className="mm-reply-input-row">
          <span className="mm-reply-mention" style={{ color: "rgba(168,139,250,0.7)" }}>
            Replying to @{comment.userId}
          </span>
          <div className="mm-reply-input-controls">
            <input type="text" value={replyText} placeholder={`Reply to ${comment.userId}...`}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleReplySubmit(index)}
              className="mm-reply-input" autoFocus
              style={{ background: "rgba(36,36,45,0.2)", color: "white" }} />
            <button className="mm-reply-submit-btn" onClick={() => handleReplySubmit(index)}
              disabled={!replyText.trim()} style={{ border: "1px solid darkslategray" }}>
              Reply
            </button>
          </div>
        </div>
      )}

      {/* Nested replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mm-replies-container">
          {comment.replies.map((reply, replyIndex) => (
            <div key={replyIndex} className="mm-reply-item">
              <div className="mm-reply-avatar">
                <span className="mm-reply-avatar-text">{(reply.userId || "?").charAt(0).toUpperCase()}</span>
              </div>
              <div className="mm-reply-content">
                <div className="mm-reply-header">
                  <strong className="mm-reply-user" style={{ color: "#a78bfa" }}>{reply.userId}</strong>
                  {reply.replyTo && reply.replyTo !== comment.userId && (
                    <span className="mm-reply-mention-inline">→ @{reply.replyTo}</span>
                  )}
                  <span className="mm-reply-time" style={{ color: "rgba(255,255,255,0.3)" }}>
                    {reply.timestamp ? getTimeAgo(reply.timestamp) : ""}
                  </span>
                </div>
                <span className="mm-reply-text" style={{ color: "rgba(255,255,255,0.85)" }}>{reply.comment}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ================================================================
  // MEDIA RENDERERS
  // ================================================================
  const renderCurrentMedia = () => {
    if (!normalizedItems || normalizedItems.length === 0) return null;
    const item = normalizedItems[currentIndex];

    switch (item.mediatype) {
      case "image":
        return (
          <div className="carousel-media-wrapper">
            <img src={item.mediaurl} alt={item.alt || "Carousel media"}
              className="carousel-media" onClick={handleMediaClick}
              onDoubleClick={handleDoubleLike} style={{ cursor: "pointer" }} />
          </div>
        );
      case "video":
        return (
          <div className="carousel-media-wrapper">
            <video src={item.mediaurl} className="carousel-media" controls playsInline
              onClick={handleMediaClick} onDoubleClick={handleDoubleLike} />
          </div>
        );
      case "audio":
        return (
          <div className="carousel-audio" onClick={handleMediaClick} onDoubleClick={handleDoubleLike}>
            <AudioWaveform src={item.mediaurl} />
            <audio src={item.mediaurl} controls />
          </div>
        );
      default:
        return null;
    }
  };

  const renderDetailMedia = (media) => {
    if (!media) return null;
    switch (media.mediatype) {
      case "image":
        return <img src={media.mediaurl} alt={media.alt || "Media"} className="carousel-detail-media" />;
      case "video":
        return <video src={media.mediaurl} className="carousel-detail-media" controls autoPlay />;
      case "audio":
        return (
          <div className="carousel-detail-audio">
            <AudioWaveform src={media.mediaurl} />
            <audio src={media.mediaurl} controls autoPlay />
          </div>
        );
      default:
        return null;
    }
  };

  // ================================================================
  // CAROUSEL NAVIGATION
  // ================================================================
  const nextItem = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % normalizedItems.length);
  };
  const prevItem = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? normalizedItems.length - 1 : prev - 1));
  };
  const handleDotClick = (index, e) => {
    e.stopPropagation();
    setCurrentIndex(index);
  };
  const handleMediaClick = (e) => {
    e.stopPropagation();
    setDetailMedia(normalizedItems[currentIndex]);
  };
  const handleDetailClose = (e) => {
    e.stopPropagation();
    setDetailMedia(null);
  };
  const handleContainerClick = (e) => { e.stopPropagation(); };

  // ================================================================
  // GUARD: EMPTY ITEMS
  // ================================================================
  if (!normalizedItems || normalizedItems.length === 0) {
    return <div className="carousel-empty">No media available</div>;
  }

  // ================================================================
  // RENDER
  // ================================================================
  return (
    <>
      {/* Carousel frame */}
      <div className="carousel-frame-container" onClick={handleContainerClick}>
        <div className="carousel-single-view">
          {renderCurrentMedia()}
          {normalizedItems[currentIndex]?.caption && (
            <div className="carousel-caption">{normalizedItems[currentIndex].caption}</div>
          )}
        </div>

        {normalizedItems.length > 1 && (
          <>
            <button className="carousel-button prev" onClick={prevItem} aria-label="Previous" type="button">‹</button>
            <button className="carousel-button next" onClick={nextItem} aria-label="Next" type="button">›</button>
            <div className="carousel-dots">
              {normalizedItems.map((_, index) => (
                <button key={index}
                  className={`carousel-dot ${index === currentIndex ? "active" : ""}`}
                  onClick={(e) => handleDotClick(index, e)} aria-label={`Go to slide ${index + 1}`}
                  type="button" />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Detail popup */}
      {detailMedia && (
        <div className="carousel-detail-overlay" onClick={handleDetailClose} style={{ background: "black" }}>
          <div className="carousel-detail-modal" onClick={(e) => e.stopPropagation()}
            style={{ border: "1px solid darkslategray" }}>

            <button className="carousel-detail-close" onClick={handleDetailClose} aria-label="Close" type="button">
              <X size={24} />
            </button>

            {/* ---------- LEFT: Media ---------- */}
            <div className="carousel-detail-media-section" style={{ background: "none" }}>
              <div style={{ alignItems: "center", justifyContent: "center", display: "flex", flex: 1 }}>
                {renderDetailMedia(detailMedia)}
              </div>
                            {/* Action bar: Like | Share | Suggest | Impressions */}
                            <div className="mm-modal-stats" style={{ background: "rgba(42,42,45,0.2)" }}>
                <div className="mm-modal-stats-left" style={{ display: "flex", flexDirection: "row", gap: 20 }}>
                  <div className="mm-modal-stat-item">
                    <span className="mm-modal-stat-icon">Like{isLiked ? " ❤️" : ""}</span>
                    <span className="mm-modal-stat-value">{likeCount}</span>
                  </div>

                  <div className="mm-modal-stat-item mm-action-btn-wrapper">
                    <button className="mm-action-btn" onClick={() => setShowShareDropdown((v) => !v)}>
                      <span>🔗 Share</span>
                      <span className="mm-modal-stat-value">{shareCount}</span>
                    </button>
                    {showShareDropdown && renderShareDropdown()}
                  </div>

                  <div className="mm-modal-stat-item">
                    <button className="mm-action-btn" onClick={() => setShowSuggestModal(true)}>
                      <span>💡 Suggest</span>
                      <span className="mm-modal-stat-value">{suggestCount}</span>
                    </button>
                  </div>
                </div>

                <div className="mm-modal-stats-right">
                  <div className="mm-modal-stat-item">
                    <span className="mm-modal-stat-icon">Impressions</span>
                    <span className="mm-modal-stat-value">{postData?.impressions || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ---------- RIGHT: Details sidebar ---------- */}
            <div className="carousel-detail-sidebar" style={{ background: "rgba(36,36,45,0.2)" }}>
              {/* Logo + Quality stars */}
              <div className="mm-logo-quality-row" style={{marginTop: 0, marginLeft: 20}}>
                <div className="mm-logo-wrap">
                  <img src={kleidoLogo} alt="Kleido" className="mm-modal-logo" />
                </div>
                {renderStars(qualityRating)}
              </div>

              {/* About section */}
              <div className="mm-about-section" style={{marginTop: 10}}>
                <h4 className="mm-modal-comments-title" style={{ color: "white" }}>About</h4>

                <div className="mm-about-row">
                  <span className="mm-about-label">Posted By:</span>
                  <span className="mm-about-value">{postData?.userName || postData?.username || "Unknown"}</span>
                </div>
                <div className="mm-about-row">
                  <span className="mm-about-label">Location:</span>
                  <span className="mm-about-value">{postData?.location || "Unknown"}</span>
                </div>
                <div className="mm-about-row">
                  <span className="mm-about-label">Time:</span>
                  <span className="mm-about-value">
                    {postData?.timestamp ? getTimeAgo(postData.timestamp) : "Just now"}
                  </span>
                </div>
                {postData?.descriptions && (
                  <div className="mm-about-row">
                    <span className="mm-about-label">Description:</span>
                    <span className="mm-about-value">{postData.descriptions}</span>
                  </div>
                )}

                <div className="mm-metadata-row">
                  <label className="mm-metadata-label">Media Label:</label>
                  <p className="mm-metadata-value mm-metadata-highlight">{mediaLabel}</p>
                </div>
                <div className="mm-metadata-row">
                  <label className="mm-metadata-label">Behavioral Depth:</label>
                  <p className="mm-metadata-value mm-metadata-highlight">{behavioralDepth}</p>
                </div>
              </div>

              {/* Comments section */}
              <div className="mm-modal-comments-section">
                <h4 className="mm-modal-comments-title" style={{ color: "white", marginLeft: 15 }}>
                  Comments ({comments.length})
                </h4>

                <div className="mm-modal-comments-list" style={{ height: 125, overflowY: "auto" }}>
                  {comments.length > 0 ? (
                    comments.map((comment, index) => renderComment(comment, index))
                  ) : (
                    <p className="mm-modal-no-comments">No comments yet. Be the first to comment!</p>
                  )}
                </div>

                <div className="mm-modal-add-comment">
                  <input type="text" value={newComment} placeholder="Add a comment..."
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
                    className="mm-modal-comment-input"
                    style={{ color: "white", background: "rgba(36,36,45,0.2)" }} />
                  <button onClick={handleAddComment} className="mm-modal-comment-button"
                    style={{ background: "rgba(36,36,45,0.2)", border: "1px solid gray", color: "white" }}>
                    Post
                  </button>
                </div>
              </div>
            </div>        
          </div>

          {showSuggestModal && renderSuggestModal()}
          {toastMsg && <div className="mm-toast">{toastMsg}</div>}
        </div>
      )}
    </>
  );
};

// ================================================================
// PROPTYPES
// ================================================================
Carousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      mediatype: PropTypes.oneOf(["image", "audio", "video"]).isRequired,
      mediaurl: PropTypes.string.isRequired,
      alt: PropTypes.string,
      caption: PropTypes.string,
      likes: PropTypes.array,
    })
  ).isRequired,
  postData: PropTypes.shape({
    postId: PropTypes.string,
    userId: PropTypes.string,
    userName: PropTypes.string,
    userimage: PropTypes.string,
    descriptions: PropTypes.string,
    location: PropTypes.string,
    country: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    likes: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
    shares: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
    suggestions: PropTypes.number,
    impressions: PropTypes.number,
    mediaLabel: PropTypes.string,
    behavioralDepth: PropTypes.string,
    qualityRating: PropTypes.number,
    shareUrl: PropTypes.string,
    currentUserId: PropTypes.string,
    comments: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.string,
        comment: PropTypes.string,
        timestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        replies: PropTypes.arrayOf(
          PropTypes.shape({
            userId: PropTypes.string,
            comment: PropTypes.string,
            timestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            replyTo: PropTypes.string,
          })
        ),
      })
    ),
  }),
  onAddComment: PropTypes.func,
  onMediaLike: PropTypes.func,
  currentUserId: PropTypes.string,
};

export default Carousel;
export { AudioWaveform };
